import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash, PencilSimple, Buildings, House, Stack, MapPin } from "@phosphor-icons/react";

const LEVELS = [
  { key: "hospital", label: "Hospital", icon: Buildings, parent: null },
  { key: "building", label: "Building", icon: House, parent: "hospital" },
  { key: "floor", label: "Floor", icon: Stack, parent: "building" },
  { key: "area", label: "Area", icon: MapPin, parent: "floor" },
];

export default function Masters() {
  const [hospitals, setHospitals] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selH, setSelH] = useState(null);
  const [selB, setSelB] = useState(null);
  const [selF, setSelF] = useState(null);
  const [modal, setModal] = useState(null); // {level, item}
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");

  const loadH = async () => { const { data } = await api.get("/masters/hospitals"); setHospitals(data); if (data[0] && !selH) setSelH(data[0].id); };
  const loadB = async (hid) => { if (!hid) { setBuildings([]); return; } const { data } = await api.get("/masters/buildings", { params: { hospital_id: hid } }); setBuildings(data); };
  const loadF = async (bid) => { if (!bid) { setFloors([]); return; } const { data } = await api.get("/masters/floors", { params: { building_id: bid } }); setFloors(data); };
  const loadA = async (fid) => { if (!fid) { setAreas([]); return; } const { data } = await api.get("/masters/areas", { params: { floor_id: fid } }); setAreas(data); };

  useEffect(() => { loadH(); }, []);
  useEffect(() => { loadB(selH); setSelB(null); setSelF(null); }, [selH]);
  useEffect(() => { loadF(selB); setSelF(null); }, [selB]);
  useEffect(() => { loadA(selF); }, [selF]);

  const openCreate = (level) => {
    const base = { active: true };
    if (level === "building") base.hospital_id = selH;
    if (level === "floor") base.building_id = selB;
    if (level === "area") { base.floor_id = selF; base.criticality = "normal"; base.area_type = "general"; base.sla_minutes = 60; base.verification_required = true; }
    setForm(base);
    setModal({ level, item: null });
    setErr("");
  };

  const openEdit = (level, item) => {
    setForm({ ...item });
    setModal({ level, item });
    setErr("");
  };

  const save = async () => {
    setErr("");
    const url = `/masters/${modal.level}s`;
    try {
      if (modal.item) {
        await api.patch(`${url}/${modal.item.id}`, form);
      } else {
        await api.post(url, form);
      }
      setModal(null);
      if (modal.level === "hospital") loadH();
      if (modal.level === "building") loadB(selH);
      if (modal.level === "floor") loadF(selB);
      if (modal.level === "area") loadA(selF);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (level, id) => {
    if (!window.confirm(`Delete this ${level}? This cannot be undone.`)) return;
    await api.delete(`/masters/${level}s/${id}`);
    if (level === "hospital") loadH();
    if (level === "building") loadB(selH);
    if (level === "floor") loadF(selB);
    if (level === "area") loadA(selF);
  };

  const [qrFor, setQrFor] = useState(null); // area object

  const Col = ({ level, items, selected, onSelect, canCreate }) => {
    const conf = LEVELS.find((l) => l.key === level);
    return (
      <div className="flat-card flex flex-col min-h-[400px]" data-testid={`masters-col-${level}`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <conf.icon size={16} className="text-slate-500" />
            <div className="label-small">{conf.label}</div>
          </div>
          {canCreate && (
            <button data-testid={`add-${level}`} onClick={() => openCreate(level)} className="p-1 rounded-md hover:bg-blue-50 text-blue-600 transition-colors">
              <Plus size={16} weight="bold" />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-4 text-xs text-slate-400 italic">
              {canCreate ? "No items. Click + to add." : "Select a parent first."}
            </div>
          ) : items.map((it) => (
            <button
              key={it.id}
              data-testid={`${level}-item-${it.id}`}
              onClick={() => onSelect && onSelect(it.id)}
              className={`w-full text-left flex items-center justify-between px-4 py-2.5 border-b border-slate-100 text-sm transition-colors group ${selected === it.id ? "bg-blue-50/60 text-blue-700 font-semibold" : "hover:bg-slate-50 text-slate-700"}`}
            >
              <span className="truncate">{it.name}{level === "hospital" && it.code ? ` · ${it.code}` : ""}{level === "area" && it.criticality === "critical" ? " 🔴" : ""}</span>
              <span className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                {level === "area" && (
                  <span data-testid={`${level}-qr-${it.id}`} onClick={(e) => { e.stopPropagation(); setQrFor(it); }} className="p-1 rounded text-slate-500 hover:text-blue-600" title="Show QR">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/><rect x="18" y="18" width="3" height="3"/></svg>
                  </span>
                )}
                <span data-testid={`${level}-edit-${it.id}`} onClick={(e) => { e.stopPropagation(); openEdit(level, it); }} className="p-1 rounded text-slate-500 hover:text-blue-600">
                  <PencilSimple size={14} />
                </span>
                <span data-testid={`${level}-delete-${it.id}`} onClick={(e) => { e.stopPropagation(); remove(level, it.id); }} className="p-1 rounded text-slate-500 hover:text-red-600">
                  <Trash size={14} />
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="masters-page">
      <header>
        <div className="label-small text-blue-700">Configuration</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>
          Hierarchy Masters
        </h1>
        <p className="text-sm text-slate-600 mt-1">Hospital › Building › Floor › Area. Click an item to drill down.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Col level="hospital" items={hospitals} selected={selH} onSelect={setSelH} canCreate />
        <Col level="building" items={buildings} selected={selB} onSelect={setSelB} canCreate={!!selH} />
        <Col level="floor" items={floors} selected={selF} onSelect={setSelF} canCreate={!!selB} />
        <Col level="area" items={areas} selected={null} onSelect={null} canCreate={!!selF} />
      </div>

      {qrFor && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => setQrFor(null)}>
          <div className="bg-white border border-slate-200 rounded-md max-w-md w-full p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
            <div className="label-small text-blue-700">Scan QR</div>
            <h3 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>{qrFor.name}</h3>
            <p className="text-xs text-slate-500 mt-1">Print or display for staff/supervisor to scan and start the area checklist.</p>
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/areas/${qrFor.id}/qr`}
              alt="QR"
              className="mx-auto my-5 w-72 h-72 border border-slate-200 rounded-md"
              data-testid="qr-image"
            />
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/areas/${qrFor.id}/qr`} download={`area-${qrFor.id}.png`} className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md transition-colors">Download QR</a>
            <button onClick={() => setQrFor(null)} className="ml-2 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-md transition-colors">Close</button>
          </div>
        </div>
      )}


      {modal && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-md max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="master-modal">
            <h3 className="text-xl font-bold text-slate-900 mb-4 capitalize" style={{ fontFamily: "Manrope" }}>
              {modal.item ? "Edit" : "New"} {modal.level}
            </h3>
            <div className="space-y-3">
              {modal.level === "hospital" && (
                <>
                  <Field label="Code" value={form.code || ""} onChange={(v) => setForm((f) => ({ ...f, code: v }))} testId="form-code" />
                  <Field label="Name" value={form.name || ""} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testId="form-name" />
                  <Field label="Display Name (brand)" value={form.display_name || ""} onChange={(v) => setForm((f) => ({ ...f, display_name: v }))} testId="form-display-name" />
                  <LogoField value={form.logo_url || ""} onChange={(v) => setForm((f) => ({ ...f, logo_url: v }))} />
                  <div>
                    <label className="label-small block mb-1">Accent Color</label>
                    <div className="flex items-center gap-2">
                      <input type="color" data-testid="form-accent-color" value={form.accent_color || "#2563EB"} onChange={(e) => setForm((f) => ({ ...f, accent_color: e.target.value }))} className="w-14 h-9 border border-slate-200 rounded-md cursor-pointer" />
                      <input value={form.accent_color || "#2563EB"} onChange={(e) => setForm((f) => ({ ...f, accent_color: e.target.value }))} className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
                    </div>
                  </div>
                  <Field label="Address" value={form.address || ""} onChange={(v) => setForm((f) => ({ ...f, address: v }))} />
                  <Field label="Contact" value={form.contact || ""} onChange={(v) => setForm((f) => ({ ...f, contact: v }))} />
                </>
              )}
              {(modal.level === "building" || modal.level === "floor") && (
                <Field label="Name" value={form.name || ""} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testId="form-name" />
              )}
              {modal.level === "area" && (
                <>
                  <Field label="Name" value={form.name || ""} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testId="form-name" />
                  <Field label="Area type" value={form.area_type || ""} onChange={(v) => setForm((f) => ({ ...f, area_type: v }))} />
                  <SelectField label="Criticality" value={form.criticality || "normal"} onChange={(v) => setForm((f) => ({ ...f, criticality: v }))} options={["critical", "normal", "optional"]} />
                  <Field label="SLA (minutes)" type="number" value={form.sla_minutes || 60} onChange={(v) => setForm((f) => ({ ...f, sla_minutes: parseInt(v) || 0 }))} />
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={!!form.verification_required} onChange={(e) => setForm((f) => ({ ...f, verification_required: e.target.checked }))} />
                    Supervisor verification required
                  </label>
                </>
              )}
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setModal(null)} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-md transition-colors">Cancel</button>
                <button data-testid="master-save" onClick={save} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md transition-colors">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="label-small block mb-1">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="label-small block mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function LogoField({ value, onChange }) {
  const [uploading, setUploading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const fileRef = React.useRef();
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("purpose", "hospital_logo");
      const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Append auth token via query for img src (no header in <img>)
      const token = localStorage.getItem("hk_token");
      onChange(`${res.data.url}?auth=${token}`);
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div data-testid="logo-field">
      <label className="label-small block mb-1">Logo</label>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="logo" className="w-12 h-12 rounded-md border border-slate-200 object-cover" data-testid="logo-preview" />
        ) : (
          <div className="w-12 h-12 rounded-md border border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-400">no logo</div>
        )}
        <div className="flex-1">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleFile} className="hidden" data-testid="logo-file-input" />
          <div className="flex gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="px-3 py-1.5 text-xs font-semibold border border-blue-200 text-blue-700 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-60" data-testid="logo-upload-button">
              {uploading ? "Uploading…" : value ? "Replace" : "Upload"}
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 rounded-md transition-colors">
                Remove
              </button>
            )}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">PNG/JPG/SVG/WEBP, max 2 MB</div>
          {err && <div className="text-xs text-red-700 mt-1">{err}</div>}
        </div>
      </div>
    </div>
  );
}
